import { InfoFooter, InfoHeader } from "../../components/InfoLayout";
import SlCloudShell from "../../brands/servicelad/SlCloudShell";
import { RelatedFreeTools, ToolFaq, toolPageSchemas } from "../../components/FreeToolSeo";
import { addOns } from "../../config/pricing";
import usePageMeta from "../../hooks/usePageMeta";
import useUlezLookup from "./useUlezLookup";
import { ulezFaqsFor } from "../../content/ulezFaqs";
import { CLEAN_AIR_ZONES } from "../../../lib/vehicle/ulezChecker";
import { formatUkReg, isValidUkReg, normalizeUkReg } from "../../../lib/vehicle/regValidation";
import infoCss from "../../styles/info.css?raw";
import ulezCss from "../../styles/ulez-sl.css?raw";

const TFL_URL = "https://tfl.gov.uk/modes/driving/ultra-low-emission-zone";
const v5cAddOn = addOns.find((item) => item.key === "v5c_alerts") || addOns[0];

const META_TITLE = "Free ULEZ Checker: Is My Car ULEZ Compliant? | ServiceLad";
const META_DESCRIPTION = "Free ULEZ checker by registration. Pop in your reg to see whether your car meets the London ULEZ standard, what it was judged on, and what a charge would cost you.";

const STANDARDS = [
  { fuel: "Petrol", needs: "Euro 4 or newer", when: "Normally 2006 onward" },
  { fuel: "Diesel", needs: "Euro 6", when: "Normally late 2015 onward" },
  { fuel: "Motorbike", needs: "Euro 3 or newer", when: "Normally 2007 onward" },
  { fuel: "Electric", needs: "Always compliant", when: "No tailpipe emissions" },
];

const STEPS = [
  ["Pop in your reg", "We check it looks like a real UK plate before anything else happens."],
  ["We read the record", "Fuel type, Euro standard, first registration date and CO2 come back for that vehicle."],
  ["You get a straight answer", "Free or charged, plus the numbers behind it so you can see why."],
];

function Verdict({ data }) {
  const { vehicle, ulez } = data;
  const chips = [
    ["Fuel", vehicle.fuelType || "Not on record"],
    ["Euro", ulez.euroStandard ? `Euro ${ulez.euroStandard}` : "Not on record"],
    ["Registered", ulez.firstRegisteredYear || "Not on record"],
    ["CO2", vehicle.co2Emissions != null ? `${vehicle.co2Emissions} g/km` : "Not on record"],
  ];

  return (
    <section className={`slz-verdict is-${ulez.status}`} aria-live="polite">
      <div className="slz-verdict-rail" aria-hidden="true" />
      <div className="slz-verdict-main">
        <div className="slz-verdict-top">
          <div>
            <span className="slz-verdict-label">{ulez.label}</span>
            <h2>{ulez.headline}</h2>
          </div>
          <div className="slz-verdict-plate">
            <span>UK</span>
            <strong>{formatUkReg(vehicle.registration)}</strong>
          </div>
        </div>

        <p className="slz-verdict-detail">{ulez.detail}</p>

        <div className="slz-verdict-chips">
          {chips.map(([label, value]) => (
            <article className="slz-chip" key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </article>
          ))}
        </div>

        <div className="slz-verdict-actions">
          <a href={TFL_URL} target="_blank" rel="noreferrer">Confirm with Transport for London</a>
          <a href={`/vehicle/${encodeURIComponent(String(vehicle.registration || "").toLowerCase())}`}>
            See the full free record
          </a>
        </div>
      </div>
    </section>
  );
}

export default function ServiceLadUlez({ brand }) {
  const { registration, setRegistration, data, error, loading, run } = useUlezLookup();
  const canonical = `https://${brand.domain}/ulez-checker`;

  usePageMeta({ title: META_TITLE, description: META_DESCRIPTION, canonical });

  const schema = {
    "@context": "https://schema.org",
    "@graph": toolPageSchemas({
      brand,
      canonical,
      name: `Free ULEZ Checker, ${brand.name}`,
      description: META_DESCRIPTION,
      alternateName: [
        "ULEZ checker",
        "Is my car ULEZ compliant",
        "ULEZ check by registration",
        "Free ULEZ check",
        "London ULEZ checker",
        "Check ULEZ by reg",
      ],
      pathLabel: "ULEZ checker",
    }),
  };

  const v5cHref = isValidUkReg(registration)
    ? `/v5c-alerts?reg=${encodeURIComponent(normalizeUkReg(registration))}`
    : "/v5c-alerts";

  return (
    <SlCloudShell brand={brand}>
      <style>{infoCss}</style>
      <style>{ulezCss}</style>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <InfoHeader brand={brand} />

      <main className="slz">
        <section className="slz-hero">
          <div className="slz-hero-inner">
            <div className="slz-hero-copy">
              <span className="slz-pill">Free ULEZ checker</span>
              <h1>Is my car ULEZ compliant?</h1>
              <p>
                Pop your reg in below. We read the fuel and Euro standard held against your
                vehicle and tell you whether London would charge you £12.50 a day, in plain
                English, with the numbers we used.
              </p>
              <div className="slz-trust">
                <span>Free to use</span>
                <span>No account</span>
                <span>Answer in seconds</span>
              </div>
            </div>

            <form
              className="slz-card slz-form"
              onSubmit={(event) => { event.preventDefault(); run(registration); }}
              noValidate
            >
              <label htmlFor="ulez-registration">Your registration</label>
              <div className="slz-plate">
                <span className="slz-plate-band" aria-hidden="true">
                  <img src="/gb-flag.svg" alt="" />
                  UK
                </span>
                <input
                  id="ulez-registration"
                  value={registration}
                  maxLength="8"
                  autoComplete="off"
                  inputMode="text"
                  placeholder="ENTER REG"
                  aria-describedby="ulez-help ulez-error"
                  onChange={(event) => setRegistration(event.target.value)}
                />
              </div>
              <button type="submit" disabled={loading}>
                {loading ? "Checking your vehicle..." : "Check my ULEZ status"}
              </button>
              <small id="ulez-help">We only use the reg to look your vehicle up.</small>
              <div className="slz-error" id="ulez-error" role="alert">{error}</div>
            </form>
          </div>
        </section>

        <div className="slz-body">
          {loading && (
            <div className="slz-loading" aria-live="polite">
              <span />
              <p>Reading your vehicle record...</p>
            </div>
          )}
          {data && <Verdict data={data} />}

          <section className="slz-card slz-answer">
            <span className="slz-kicker">Straight answer</span>
            <h2>What makes a car ULEZ compliant?</h2>
            <p>
              The Euro emissions standard the vehicle was built to, and nothing else. A petrol
              car needs Euro 4, a diesel needs Euro 6, and a petrol motorbike needs Euro 3.
              Fully electric and hydrogen vehicles are compliant because they have no tailpipe
              emissions. Anything that misses its standard pays £12.50 for each day it is
              driven inside the zone, which now covers every London borough.
            </p>
            <a className="slz-link" href="/guides/ulez-explained">Read our full ULEZ guide</a>
          </section>

          <section className="slz-standards">
            <div className="slz-head">
              <span className="slz-kicker">The rules</span>
              <h2>What your fuel type has to meet</h2>
            </div>
            <div className="slz-standard-grid">
              {STANDARDS.map((standard) => (
                <article className="slz-card slz-standard" key={standard.fuel}>
                  <span className="slz-standard-fuel">{standard.fuel}</span>
                  <strong>{standard.needs}</strong>
                  <small>{standard.when}</small>
                </article>
              ))}
            </div>
            <p className="slz-note">
              Hybrids are judged on their petrol or diesel side, which normally passes. Anything
              over 3,500kg follows the London LEZ instead, and that daily charge is far higher.
            </p>
          </section>

          <section className="slz-steps">
            <div className="slz-head">
              <span className="slz-kicker">How it works</span>
              <h2>Three steps, no sign up</h2>
            </div>
            <ol>
              {STEPS.map(([title, copy], index) => (
                <li key={title}>
                  <span className="slz-step-number">{index + 1}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{copy}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="slz-zones">
            <div className="slz-head">
              <span className="slz-kicker">Where you pay</span>
              <h2>UK zones that charge private cars</h2>
            </div>
            <div className="slz-zone-grid">
              {CLEAN_AIR_ZONES.map((zone) => (
                <article className="slz-card slz-zone" key={zone.key}>
                  <h3>{zone.name}</h3>
                  <p>{zone.area}</p>
                  <strong>{zone.charge}</strong>
                  <a href={zone.checkUrl} target="_blank" rel="noreferrer">{zone.checkLabel}</a>
                </article>
              ))}
            </div>
            <p className="slz-note">
              Bradford, Portsmouth, Newcastle, Sheffield and Tyneside run zones that leave
              private cars alone. Our checker uses the London rules, the strictest of the three
              that do charge cars.
            </p>
          </section>

          <section className="slz-card slz-bridge">
            <div>
              <span className="slz-kicker">Keeping an eye on it</span>
              <h2>Your ULEZ answer will not change. Your vehicle record can.</h2>
              <p>
                A ULEZ verdict comes from emissions data that stays put. What moves is
                everything else held against your reg, and a reissued V5C logbook is the one
                worth knowing about, because it is what a sale or a cloning attempt looks like
                from the outside.
              </p>
            </div>
            <div className="slz-bridge-offer">
              <strong>{v5cAddOn.name}</strong>
              <p>{v5cAddOn.copy}</p>
              <div className="slz-bridge-price">
                <span>From</span>
                <b>£{v5cAddOn.plate}</b>
              </div>
              <a href={v5cHref}>Set up alerts</a>
            </div>
          </section>

          <ToolFaq title="ULEZ questions drivers ask" items={ulezFaqsFor(brand.key)} />
          <RelatedFreeTools current="ulez" />

          <aside className="slz-disclaimer">
            <strong>This is a guide, not the final word.</strong>
            <span>
              We work from the free registration record. Transport for London keeps its own
              database and has the final say on any single vehicle, so confirm your reg with
              TfL before you drive into the zone.
            </span>
          </aside>
        </div>
      </main>

      <InfoFooter brand={brand} />
    </SlCloudShell>
  );
}
